// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 2.0
// updated  : 2026-06-20
// protected: NO
// changelog: v2.0 (Parity P0-4) - lifecycle V1: create=DRAFT (Disiapkan) -> dispatch
//            -> receive -> cancel. Tombol per role, timeline, PDF surat jalan/tanda
//            terima. Stok real-time dari ledger view (bukan cache FE).
// =============================================================================
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/v2/components/statusMap";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, Send, PackageCheck, RefreshCw, ArrowRight, CheckCircle2, XCircle,
  FileText, FileCheck2, Truck, Ban, PlusCircle, ImagePlus, Camera, Inbox, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { QrBatchInput } from "@/v2/components/QrBatchInput";
import { StockPanel } from "@/v2/components/StockPanel";
import { resultToast, isOwner, openPdf } from "@/v2/components/helpers";
import { EmptyState, TableSkeletonRows, ListSkeleton, useDelayedLoading } from "@/v2/components/ux";
import { ListPager, loadStoredPageSize } from "@/v2/components/ListPager";

const STATUS_LABEL = { DRAFT: "Disiapkan", SHIPPED: "Dikirim", RECEIVED: "Diterima", CLOSED: "Ditutup", CANCELLED: "Dibatalkan" };
const fmt = (s) => { if (!s) return null; try { return new Date(s).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" }); } catch (e) { return s; } };

export const DocumentFlow = ({
  title, icon: Icon, listPath, createPath, dispatchPath, receivePath, cancelPath,
  buildBody, docType, sourceLabel, targetLabel, sourceOptions, targetOptions,
  defaultSource, defaultTarget, hasReason = false,
  canCreate, canDispatch, canReceive, canCancel,
  receiverLocation, createLabel = "Buat Dokumen (Disiapkan)", testid,
  enablePhotos = false, photosPath,
  emptyTitle, emptyDescription,
  extraCreateSection,   // RETUR PASCA-UAT: render-prop opsional di form create ({source, qrs, setQrs, createdTick})
  linesPath,            // RETUR PASCA-UAT: path rincian per-baris (CACAT/biasa) utk dialog detail
}) => {
  const { me, api, authHeader, API } = useV2Auth();
  const owner = isOwner(me?.role);
  const [source, setSource] = useState(defaultSource);
  const [target, setTarget] = useState(defaultTarget);
  const [reason, setReason] = useState("");
  const [qrs, setQrs] = useState([]);
  const [docs, setDocs] = useState([]);
  // PAGINASI T1 (khusus loadDocs + render pager; logika lain tak disentuh).
  const pagerKey = `docs:${testid}`;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => loadStoredPageSize(pagerKey));
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [reloadTick, setReloadTick] = useState(0);
  const listTopRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [actingId, setActingId] = useState(null);

  // FASE 3B.2: dialog foto retur (aktif hanya bila enablePhotos).
  const [photoDoc, setPhotoDoc] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [detailDoc, setDetailDoc] = useState(null);  // Tahap 3 UX: detail dokumen read-only (klik baris/kartu)
  const [createdTick, setCreatedTick] = useState(0); // RETUR PASCA-UAT: sinyal reload panel barang cacat
  const [detailLines, setDetailLines] = useState(null);   // RETUR PASCA-UAT: rincian baris dokumen
  const [linesLoading, setLinesLoading] = useState(false);

  const loadDocs = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const sep = listPath.includes("?") ? "&" : "?";
      const r = await api(`${listPath}${sep}page=${page}&page_size=${pageSize}`);
      if (r.ok) {
        const data = await r.json();
        // Toleran dua bentuk: array (endpoint belum berpaginasi) atau objek paginasi.
        const list = Array.isArray(data) ? data : (data.items ?? []);
        setDocs(list);
        setTotal(Array.isArray(data) ? list.length : (data.total || 0));
        setPages(Array.isArray(data) ? 1 : (data.pages || 1));
      } else { setDocs([]); setTotal(0); setPages(1); }
    } finally { setLoadingDocs(false); }
  }, [api, listPath, page, pageSize, reloadTick]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  const goPage = (p) => {
    setPage(p);
    if (listTopRef.current) listTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const changePageSize = (s) => { setPageSize(s); setPage(1); };

  // RETUR PASCA-UAT: muat rincian per-baris (CACAT/biasa) saat dialog detail dibuka.
  useEffect(() => {
    if (!detailDoc || !linesPath) { setDetailLines(null); return; }
    let alive = true;
    (async () => {
      setLinesLoading(true);
      try {
        const r = await api(linesPath(detailDoc.id));
        const data = await r.json();
        if (alive) setDetailLines(r.ok ? data : null);
      } catch (e) { if (alive) setDetailLines(null); }
      finally { if (alive) setLinesLoading(false); }
    })();
    return () => { alive = false; };
  }, [detailDoc, linesPath, api]);

  const showSkeleton = useDelayedLoading(loadingDocs && docs.length === 0);
  const emptyT = emptyTitle || `Belum ada ${(title || "dokumen").toLowerCase()}`;
  const emptyD = emptyDescription || (canCreate
    ? "Buat dokumen baru pada panel di samping untuk memulai."
    : "Dokumen akan muncul di sini setelah dibuat dan diproses oleh peran terkait.");

  const validTargets = useMemo(() => targetOptions(source), [source, targetOptions]);
  useEffect(() => {
    if (validTargets.length && !validTargets.includes(target)) setTarget(validTargets[0]);
  }, [validTargets, target]);

  const stockLoc = canCreate ? source : receiverLocation;

  const create = async () => {
    if (qrs.length === 0) { toast.error("Scan minimal 1 QR"); return; }
    setBusy(true);
    try {
      const r = await api(createPath || listPath, {
        method: "POST", body: JSON.stringify(buildBody({ source, target, reason, qrs })) });
      const data = await r.json();
      if (!r.ok) { toast.error(typeof data.detail === "string" ? data.detail : "Gagal membuat dokumen"); return; }
      toast.success(`Dokumen ${data.no || ""} dibuat (Disiapkan) · ${data.planned_count} item${data.defect_count ? ` (${data.defect_count} cacat)` : ""}`);
      setQrs([]); setReason(""); setCreatedTick((t) => t + 1);
      setPage(1); setReloadTick((t) => t + 1);  // dokumen baru pasti paling atas -> halaman 1
    } finally { setBusy(false); }
  };

  const act = async (doc, pathFn, verb) => {
    setActingId(doc.id + verb);
    try {
      const r = await api(pathFn(doc.id), { method: "POST", body: JSON.stringify({}) });
      const data = await r.json();
      if (!r.ok) { toast.error(typeof data.detail === "string" ? data.detail : `Gagal ${verb}`); return; }
      if (data.result) resultToast(toast, data, verb);
      else if (data.reversed !== undefined) toast.success(`Dibatalkan · ${data.reversed} item dikembalikan ke sumber`);
      else toast.success(`Dokumen ${verb}`);
      setReloadTick((t) => t + 1);  // tetap di halaman yang sedang dibuka
    } finally { setActingId(null); }
  };

  // FASE 3B.2: buka dialog + muat foto retur.
  const openPhotos = async (doc) => {
    setPhotoDoc(doc); setPhotos([]); setPhotoLoading(true);
    try {
      const r = await api(photosPath(doc.id));
      const data = await r.json();
      if (r.ok) setPhotos(data.photos || []);
      else toast.error(typeof data.detail === "string" ? data.detail : "Gagal memuat foto");
    } finally { setPhotoLoading(false); }
  };

  const uploadPhotos = async (fileList) => {
    if (!photoDoc || !fileList || fileList.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(fileList).forEach((f) => fd.append("files", f));
      const h = { ...authHeader() };
      delete h["Content-Type"]; // biarkan browser set multipart boundary
      const r = await fetch(`${API}${photosPath(photoDoc.id)}`, { method: "POST", body: fd, headers: h });
      const data = await r.json();
      if (!r.ok) { toast.error(typeof data.detail === "string" ? data.detail : "Gagal unggah foto"); return; }
      toast.success(`${data.added} foto diunggah (total ${data.photo_count})`);
      await openPhotos(photoDoc);
      setReloadTick((t) => t + 1);  // tetap di halaman saat ini
    } finally { setUploading(false); }
  };

  // Fase 4C: aksi dokumen dipakai ulang di tabel (desktop) & card (mobile). sfx=suffix testid unik.
  const renderActions = (d, sfx = "") => {
    const to = d.to_location || d.to_store;
    const shipped = d.status !== "DRAFT" && d.status !== "CANCELLED";
    return (
      <>
        {docType && shipped && (
          <Button size="sm" variant="ghost" onClick={() => openPdf(api, docType, d.id, d.no)} data-testid={`${testid}-pdf${sfx}`} title="Surat Jalan PDF">
            <FileText className="w-4 h-4" />
          </Button>
        )}
        {docType && (d.status === "RECEIVED" || d.status === "CLOSED") && (
          <Button size="sm" variant="ghost" onClick={() => openPdf(api, "tanda-terima", d.id, `TT-${d.no}`)} data-testid={`${testid}-tt${sfx}`} title="Tanda Terima PDF">
            <FileCheck2 className="w-4 h-4" />
          </Button>
        )}
        {enablePhotos && (
          <Button size="sm" variant="ghost" onClick={() => openPhotos(d)} data-testid={`${testid}-photos${sfx}`} title="Foto Retur">
            <Camera className="w-4 h-4" />
            {d.photo_count > 0 && <span className="ml-1 text-xs">{d.photo_count}</span>}
          </Button>
        )}
        {canDispatch && d.status === "DRAFT" && (
          <Button size="sm" onClick={() => act(d, dispatchPath, "dispatch")} disabled={actingId === d.id + "dispatch"} data-testid={`${testid}-dispatch${sfx}`}>
            {actingId === d.id + "dispatch" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Truck className="w-4 h-4 mr-1" />}Konfirmasi Kirim
          </Button>
        )}
        {canReceive && d.status === "SHIPPED" && (!receiverLocation || to === receiverLocation) && (
          <Button size="sm" variant="outline" onClick={() => act(d, receivePath, "diterima")} disabled={actingId === d.id + "diterima"} data-testid={`${testid}-receive${sfx}`}>
            {actingId === d.id + "diterima" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PackageCheck className="w-4 h-4 mr-1" />}Terima
          </Button>
        )}
        {canCancel && (d.status === "DRAFT" || d.status === "SHIPPED") && (
          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => act(d, cancelPath, "dibatalkan")} disabled={actingId === d.id + "dibatalkan"} data-testid={`${testid}-cancel${sfx}`}>
            {actingId === d.id + "dibatalkan" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
          </Button>
        )}
      </>
    );
  };

  return (
    <div className="space-y-5" data-testid={testid}>
      <h2 className="text-2xl font-semibold flex items-center gap-2">{Icon && <Icon className="w-6 h-6" />}{title}</h2>

      <div className="grid lg:grid-cols-2 gap-4">
        {canCreate ? (
          <Card className="p-5 rounded-2xl space-y-4">
            <div className="flex items-center gap-2"><PlusCircle className="w-4 h-4 text-primary" /><h3 className="font-semibold">{createLabel}</h3></div>
            <p className="text-xs text-muted-foreground">Dokumen dibuat sebagai <b>Disiapkan</b> (stok belum berubah). Stok baru keluar saat <b>Konfirmasi Kirim</b>.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{sourceLabel}</Label>
                <Select value={source} onValueChange={setSource} disabled={!owner}>
                  <SelectTrigger className="mt-1" data-testid={`${testid}-source`}><SelectValue /></SelectTrigger>
                  <SelectContent>{sourceOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{targetLabel}</Label>
                <Select value={target} onValueChange={setTarget}>
                  <SelectTrigger className="mt-1" data-testid={`${testid}-target`}><SelectValue /></SelectTrigger>
                  <SelectContent>{validTargets.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {hasReason && (
              <div>
                <Label className="text-xs">Alasan (opsional)</Label>
                <Input className="mt-1" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="cth: cacat produksi" data-testid={`${testid}-reason`} />
              </div>
            )}
            {extraCreateSection && extraCreateSection({ source, qrs, setQrs, createdTick })}
            <div>
              <Label className="text-xs">Merchandize (scan QR)</Label>
              <div className="mt-1"><QrBatchInput value={qrs} onChange={setQrs} testid={`${testid}-qr`} /></div>
            </div>
            <Button onClick={create} disabled={busy} data-testid={`${testid}-submit`}>
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlusCircle className="w-4 h-4 mr-1" />}{createLabel}
            </Button>
          </Card>
        ) : (
          <Card className="p-5 rounded-2xl grid place-items-center text-center text-sm text-muted-foreground min-h-[160px]" data-testid={`${testid}-readonly`}>
            {owner
              ? "Mode Owner: read-only (audit/monitoring). Aksi operasional (buat/dispatch/terima/batal) dinonaktifkan sesuai kebijakan akses."
              : "Role Anda hanya dapat melihat / menerima dokumen pada modul ini."}
          </Card>
        )}

        <StockPanel location={stockLoc} title="Stok real-time (sumber)" />
      </div>

      <Card className="p-5 rounded-2xl" ref={listTopRef}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Daftar Dokumen</h3>
          <Button size="icon" variant="ghost" onClick={loadDocs} data-testid={`${testid}-refresh`}><RefreshCw className="w-4 h-4" /></Button>
        </div>
        <div className="hidden md:block overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>No</TableHead><TableHead>Rute</TableHead><TableHead>Status / Timeline</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {showSkeleton && docs.length === 0 && <TableSkeletonRows rows={4} cols={4} testid={`${testid}-skeleton`} />}
              {!showSkeleton && docs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="p-0">
                    <EmptyState icon={Inbox} title={emptyT} description={emptyD} testid={`${testid}-empty`} />
                  </TableCell>
                </TableRow>
              )}
              {docs.map((d) => {
                const from = d.from_location || d.from_store;
                const to = d.to_location || d.to_store;
                return (
                  <TableRow key={d.id} data-testid={`${testid}-row`}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => setDetailDoc(d)}
                    tabIndex={0} role="button"
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetailDoc(d); } }}>
                    <TableCell className="font-mono text-xs align-top">{d.no}
                      {d.defect_count > 0 && <div><Badge variant="destructive" className="text-[9px] px-1.5 py-0 mt-0.5">{d.defect_count} cacat</Badge></div>}
                      {d.status === "DRAFT" && <div className="text-[10px] text-muted-foreground">{d.planned_count} item rencana</div>}
                    </TableCell>
                    <TableCell className="text-sm align-top"><span className="inline-flex items-center gap-1">{from}<ArrowRight className="w-3 h-3 text-muted-foreground" />{to}</span></TableCell>
                    <TableCell className="align-top">
                      <StatusBadge status={d.status} label={STATUS_LABEL[d.status] || d.status} testid={`${testid}-status`} />
                      <div className="text-[10px] text-muted-foreground mt-1 space-y-0.5" data-testid={`${testid}-timeline`}>
                        {d.created_at && <div>Disiapkan: {fmt(d.created_at)}</div>}
                        {d.shipped_at && <div>Dikirim: {fmt(d.shipped_at)}</div>}
                        {d.received_at && <div>Diterima: {fmt(d.received_at)}</div>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right space-x-1 whitespace-nowrap align-top" onClick={(e) => e.stopPropagation()}>
                      {renderActions(d)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* Mobile: card list dokumen */}
        <div className="md:hidden space-y-3" data-testid={`${testid}-cards`}>
          {showSkeleton && docs.length === 0 && <ListSkeleton rows={3} testid={`${testid}-skeleton-m`} />}
          {!showSkeleton && docs.length === 0 && (
            <EmptyState icon={Inbox} title={emptyT} description={emptyD} testid={`${testid}-empty-m`} className="rounded-xl border border-dashed" />
          )}
          {docs.map((d) => {
            const from = d.from_location || d.from_store;
            const to = d.to_location || d.to_store;
            return (
              <div key={d.id} className="rounded-xl border p-3 space-y-2 cursor-pointer hover:bg-muted/50 transition-colors" data-testid={`${testid}-row-m`}
                onClick={() => setDetailDoc(d)}
                tabIndex={0} role="button"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setDetailDoc(d); } }}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs">{d.no}{d.defect_count > 0 && <Badge variant="destructive" className="ml-1.5 text-[9px] px-1.5 py-0">{d.defect_count} cacat</Badge>}</span>
                  <StatusBadge status={d.status} label={STATUS_LABEL[d.status] || d.status} />
                </div>
                <div className="text-sm inline-flex items-center gap-1">{from}<ArrowRight className="w-3 h-3 text-muted-foreground" />{to}</div>
                {d.status === "DRAFT" && <div className="text-[10px] text-muted-foreground">{d.planned_count} item rencana</div>}
                <div className="text-[10px] text-muted-foreground space-y-0.5">
                  {d.created_at && <div>Disiapkan: {fmt(d.created_at)}</div>}
                  {d.shipped_at && <div>Dikirim: {fmt(d.shipped_at)}</div>}
                  {d.received_at && <div>Diterima: {fmt(d.received_at)}</div>}
                </div>
                <div className="flex flex-wrap gap-2 pt-1 border-t" onClick={(e) => e.stopPropagation()}>
                  {renderActions(d, "-m")}
                </div>
              </div>
            );
          })}
        </div>

        {(total > 0 || loadingDocs) && (
          <ListPager
            page={page} pageSize={pageSize} total={total} pages={pages} loading={loadingDocs}
            onPageChange={goPage} onPageSizeChange={changePageSize}
            storageKey={pagerKey} testid={`${testid}-pager`}
          />
        )}
      </Card>

      {enablePhotos && (
        <Dialog open={!!photoDoc} onOpenChange={(o) => { if (!o) setPhotoDoc(null); }}>
          <DialogContent className="max-w-2xl" data-testid={`${testid}-photo-dialog`}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5" /> Foto Retur {photoDoc?.no}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Format: JPEG/PNG/WebP · maks 10MB/file · maks 10 foto per retur.
              </p>
              {canCreate && (
                <div>
                  <label htmlFor={`${testid}-photo-input`} className="inline-flex">
                    <Button asChild size="sm" disabled={uploading} data-testid={`${testid}-photo-upload-btn`}>
                      <span>
                        {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-1" />}
                        Unggah Foto
                      </span>
                    </Button>
                  </label>
                  <input
                    id={`${testid}-photo-input`}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                    data-testid={`${testid}-photo-input`}
                    onChange={(e) => { uploadPhotos(e.target.files); e.target.value = ""; }}
                  />
                </div>
              )}
              {photoLoading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Memuat foto...
                </div>
              ) : photos.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-8" data-testid={`${testid}-photo-empty`}>
                  Belum ada foto untuk retur ini.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid={`${testid}-photo-grid`}>
                  {photos.map((p) => (
                    <a key={p.key} href={p.url} target="_blank" rel="noopener noreferrer"
                       className="block rounded-lg overflow-hidden border hover:opacity-90 transition-opacity"
                       data-testid={`${testid}-photo-item`}>
                      <img src={p.url} alt="Foto retur" className="w-full h-28 object-cover" loading="lazy" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Tahap 3 UX: Detail dokumen read-only (klik baris/kartu). Aksi tetap di baris daftar. */}
      <Dialog open={!!detailDoc} onOpenChange={(o) => !o && setDetailDoc(null)}>
        <DialogContent data-testid={`${testid}-detail-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />Detail · {detailDoc?.no}
            </DialogTitle>
          </DialogHeader>
          {detailDoc && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">Status</span>
                <StatusBadge status={detailDoc.status} label={STATUS_LABEL[detailDoc.status] || detailDoc.status} testid={`${testid}-detail-status`} />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">Rute</span>
                <span className="inline-flex items-center gap-1 font-medium">
                  {detailDoc.from_location || detailDoc.from_store}
                  <ArrowRight className="w-3 h-3 text-muted-foreground" />
                  {detailDoc.to_location || detailDoc.to_store}
                </span>
              </div>
              {detailDoc.reason && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground">Alasan</span>
                  <span className="font-medium text-right">{detailDoc.reason}</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">Jumlah item</span>
                <span className="font-medium">{detailDoc.planned_count ?? "-"}
                  {detailDoc.defect_count > 0 && <Badge variant="destructive" className="ml-2 text-[9px] px-1.5 py-0">{detailDoc.defect_count} cacat</Badge>}
                </span>
              </div>
              {linesPath && (
                linesLoading ? (
                  <div className="text-xs text-muted-foreground flex items-center gap-2 py-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Memuat rincian barang...</div>
                ) : (detailLines?.lines || []).length > 0 && (
                  <div className="rounded-lg border divide-y max-h-48 overflow-y-auto" data-testid={`${testid}-detail-lines`}>
                    {detailLines.lines.map((l) => (
                      <div key={l.qr_id} className="px-3 py-2 text-xs flex items-start justify-between gap-2" data-testid={`${testid}-detail-line`}>
                        <span className="min-w-0">
                          <span className="block font-medium truncate">{l.product_name || "-"} <span className="text-muted-foreground font-normal">{l.size || "-"}{l.club ? ` · ${l.club}` : ""}</span></span>
                          <span className="block font-mono text-[10px] text-muted-foreground truncate">{l.qr_id}</span>
                        </span>
                        {l.defect
                          ? <Badge variant="destructive" className="text-[9px] shrink-0">{l.line_status || "CACAT"}</Badge>
                          : <Badge variant="secondary" className="text-[9px] shrink-0">Retur biasa</Badge>}
                      </div>
                    ))}
                  </div>
                )
              )}
              <div className="rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground space-y-0.5" data-testid={`${testid}-detail-timeline`}>
                {detailDoc.created_at && <div>Disiapkan: {fmt(detailDoc.created_at)}</div>}
                {detailDoc.shipped_at && <div>Dikirim: {fmt(detailDoc.shipped_at)}</div>}
                {detailDoc.received_at && <div>Diterima: {fmt(detailDoc.received_at)}</div>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDoc(null)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
